import type { ComponentProps } from 'react';
import type React from 'react';

import { Button, ChangeDot, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { parseHtml } from '@change-corgi/core/react/html';

import type { HttpError } from 'src/shared/error';

import { useErrorText } from 'src/app/shared/hooks/error';

type Status = HttpError['code'] | 500;

type Props = Readonly<{
	status: Status;
	errorId?: string;
}>;

export const ErrorPageContainer: React.FC<Props & ComponentProps<'div'>> = ({
	status,
	errorId,
	className,
	...rest
}) => {
	const i18n = useErrorText(status);

	return (
		<div
			className={cn('container flex min-h-[60vh] w-full flex-col items-center justify-center px-6 py-16', className)}
			{...rest}
		>
			<Text
				variant="display"
				as="h1"
				className="mb-8 inline-flex items-end gap-2 text-center leading-none"
				data-qa="error-page-header"
			>
				{parseHtmlSafe(i18n.header)}
				<div className="mb-1 -ml-1">
					<ChangeDot size="medium" />
				</div>
			</Text>

			<Text as="p" className="mb-8 max-w-2xl text-center" size="large" data-qa="error-page-main">
				{parseHtmlSafe(i18n.main)}
			</Text>

			{i18n.back_to_home && (
				<Button as="a" href="/" variant="primary">
					{parseHtmlSafe(i18n.back_to_home)}
				</Button>
			)}

			{errorId && (
				<div className="mt-8">
					<Text size="x-small" className="text-typography-weak">
						Error ID: {errorId}
					</Text>
				</div>
			)}
		</div>
	);
};

function parseHtmlSafe(html: string) {
	try {
		return parseHtml(html);
	} catch (e) {
		return '';
	}
}
